import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  IconButton,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Typography,
  Divider,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import LaunchIcon from '@mui/icons-material/Launch';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import dataProvider from '../../providers/dataProvider';

export const SelectedVideoDisplay = ({ video, onClear }) => {
  if (!video) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        mb: 1,
        bgcolor: 'success.light',
        borderRadius: 1,
      }}
    >
      <Typography variant="body2" sx={{ flex: 1 }}>
        Selected: <strong>{video.originalName || video.fileName || `Video #${video.id}`}</strong>
        {video.muxPlaybackId && ` (${video.muxPlaybackId})`}
      </Typography>
      {video.muxPlaybackId && (
        <IconButton
          href={`https://stream.mux.com/${video.muxPlaybackId}/low.mp4`}
          target="_blank"
          rel="noopener noreferrer"
          size="small"
          title="Preview video"
        >
          <LaunchIcon fontSize="small" />
        </IconButton>
      )}
      <IconButton size="small" onClick={onClear} title="Clear selection">
        <ClearIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

const PAGE_SIZE = 10;

const VideoPickerDialog = ({ open, onClose, onSelect }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');

  const totalPages = useMemo(() => (total ? Math.ceil(total / PAGE_SIZE) : 1), [total]);

  const fetchVideos = useCallback(async () => {
    if (!open) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await dataProvider.getList('file/video', {
        pagination: { page, perPage: PAGE_SIZE },
        sort: { field: 'createdAt', order: 'DESC' },
        filter: query ? { search: query } : {},
      });
      setVideos(response?.data || []);
      setTotal(response?.total || 0);
    } catch (err) {
      setError('Unable to load videos. Please try again.');
      setVideos([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [open, page, query]);

  useEffect(() => {
    if (!open) {
      return;
    }
    fetchVideos();
  }, [open, fetchVideos]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setQuery(searchInput.trim());
  };

  const handleSelectVideo = (video) => {
    onSelect(video);
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  const handleExited = () => {
    setVideos([]);
    setSearchInput('');
    setQuery('');
    setPage(1);
    setError('');
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionProps={{ onExited: handleExited }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Select a Video from Media</DialogTitle>
      <DialogContent>
        <form
          onSubmit={handleSearchSubmit}
          style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}
        >
          <TextField
            fullWidth
            label="Search by name or Mux ID"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            variant="outlined"
            size="small"
          />
          <Button type="submit" color="primary" variant="contained">
            Search
          </Button>
        </form>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
            <CircularProgress size={24} />
          </div>
        ) : (
          <>
            {error ? (
              <Typography color="error" style={{ marginBottom: 12 }}>
                {error}
              </Typography>
            ) : null}
            {videos.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No videos found. Try a different search or upload via Media → Videos.
              </Typography>
            ) : (
              <List dense>
                {videos.map((video) => (
                  <Fragment key={video.id}>
                    <ListItem
                      button
                      alignItems="flex-start"
                      onClick={() => handleSelectVideo(video)}
                    >
                      <ListItemText
                        primary={video.originalName || video.fileName || `Video #${video.id}`}
                        secondary={[
                          video.muxPlaybackId ? `Mux ID: ${video.muxPlaybackId}` : null,
                          video.duration ? `Duration: ${video.duration}s` : null,
                        ]
                          .filter(Boolean)
                          .join(' • ')}
                      />
                      <ListItemSecondaryAction>
                        {video.muxPlaybackId ? (
                          <IconButton
                            href={`https://stream.mux.com/${video.muxPlaybackId}/low.mp4`}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            title="Open stream in new tab"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <LaunchIcon fontSize="small" />
                          </IconButton>
                        ) : null}
                        <Button
                          color="primary"
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleSelectVideo(video);
                          }}
                        >
                          Select
                        </Button>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider component="li" />
                  </Fragment>
                ))}
              </List>
            )}
            {total > PAGE_SIZE ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 12,
                }}
              >
                <Button disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  Previous
                </Button>
                <Typography variant="body2">
                  Page {page} / {totalPages}
                </Typography>
                <Button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => (p < totalPages ? p + 1 : p))}
                >
                  Next
                </Button>
              </div>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const VideoPickerButton = ({ onSelect, label = 'Browse media library', style }) => {
  const [isPickerOpen, setPickerOpen] = useState(false);

  const handleSelect = useCallback(
    (video) => {
      if (onSelect) {
        onSelect(video);
      }
    },
    [onSelect],
  );

  return (
    <>
      <Button
        type="button"
        variant="outlined"
        size="small"
        startIcon={<VideoLibraryIcon />}
        onClick={() => setPickerOpen(true)}
        style={style}
      >
        {label}
      </Button>
      <VideoPickerDialog
        open={isPickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handleSelect}
      />
    </>
  );
};

export default VideoPickerButton;
